import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { collection, doc, getDoc, getDocs } from 'firebase/firestore'
import { db } from '../firebase-config'
import { formatVideoUrl } from '../utils/video'
import { estimateMinutes, isWorkoutDoc } from '../utils/workout'
import Seo from '../components/Seo'
import { site, breadcrumbSchema, exerciseSchema } from '../seo'

// Search results cut off around 155 characters, so trim on a word boundary
// rather than letting Google truncate mid-word.
const truncate = (text, max = 155) => {
  const clean = (text || '').replace(/\s+/g, ' ').trim()
  if (clean.length <= max) return clean
  return `${clean.slice(0, clean.lastIndexOf(' ', max))}…`
}

function ExerciseDetail() {
  const { id } = useParams()
  const [exercise, setExercise] = useState(null)
  const [usedIn, setUsedIn] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchExercise = async () => {
      setLoading(true)
      setError(null)

      try {
        const docRef = doc(db, 'exercises', id)
        const docSnap = await getDoc(docRef)

        if (!docSnap.exists()) {
          setError('Exercise not found')
          setLoading(false)
          return
        }

        setExercise({ id: docSnap.id, ...docSnap.data() })
        setLoading(false)

        // Which workouts feature this exercise? Firestore can't query inside an
        // array of maps, so filter client side — the collection is small.
        const workoutsSnapshot = await getDocs(collection(db, 'workouts'))
        const matches = workoutsSnapshot.docs
          .map(workoutDoc => ({ id: workoutDoc.id, ...workoutDoc.data() }))
          .filter(workout => isWorkoutDoc(workout) &&
            workout.items.some(item => item.exerciseId === id))
        setUsedIn(matches)
      } catch (err) {
        console.error('Error fetching exercise:', err)
        setError('Error loading exercise')
        setLoading(false)
      }
    }

    fetchExercise()
  }, [id])

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 pt-32 flex justify-center">
        <div className="text-xl">Loading exercise...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-16 pt-32">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error}</p>
          <Link to="/exercises" className="underline mt-2 inline-block">
            Back to the exercise library
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-16 pt-32">
      {exercise && (
        <div className="max-w-4xl mx-auto">
          <Seo
            title={`${exercise.title} | Exercise Library | ${site.siteName}`}
            description={truncate(
              exercise.description ||
                `How to perform ${exercise.title}, demonstrated by Surrey personal trainer Alex Albert.`
            )}
            type="article"
            schema={[
              exerciseSchema(exercise),
              breadcrumbSchema([
                { name: 'Home', path: '/' },
                { name: 'Exercise Library', path: '/exercises' },
                { name: exercise.title, path: `/exercises/${exercise.id}` }
              ])
            ]}
          />
          <Link to="/exercises" className="text-indigo-600 hover:text-indigo-800 mb-6 inline-block">
            &larr; Back to the exercise library
          </Link>

          <h1 className="text-4xl font-bold mb-4">{exercise.title}</h1>

          <div className="flex flex-wrap gap-2 mb-6">
            <span className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
              {exercise.difficulty}
            </span>
            {exercise.duration && (
              <span className="bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full">
                {exercise.duration} min
              </span>
            )}
            <span className="bg-purple-100 text-purple-800 text-sm px-3 py-1 rounded-full">
              {exercise.category}
            </span>
          </div>

          {exercise.videoUrl && (
            <div className="mb-8">
              <div className="aspect-w-16 aspect-h-9 bg-gray-100 rounded-lg overflow-hidden">
                <iframe
                  src={formatVideoUrl(exercise.videoUrl)}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={`${exercise.title} video`}
                ></iframe>
              </div>
            </div>
          )}

          <div className="prose max-w-none mb-12">
            <h2 className="text-2xl font-semibold mb-4">How to do it</h2>
            <p className="text-gray-700 whitespace-pre-line">{exercise.description}</p>
          </div>

          {usedIn.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-semibold mb-4">Featured in these workouts</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {usedIn.map(workout => (
                  <Link
                    key={workout.id}
                    to={`/workouts/${workout.id}`}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
                  >
                    <h3 className="font-bold mb-1">{workout.title}</h3>
                    <p className="text-sm text-gray-500">
                      {workout.items.length} exercises &middot; about {estimateMinutes(workout)} min
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Call to Action Banner */}
          <div className="bg-indigo-100 rounded-lg p-6 mb-8">
            <h3 className="text-xl font-bold text-indigo-800 mb-2">
              Want personalised guidance?
            </h3>
            <p className="text-indigo-700 mb-4">
              Book a consultation with our expert trainers to get a customised workout plan tailored to your specific needs and goals.
            </p>
            <Link
              to="/book-consultation"
              className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
            >
              Book a Consultation
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

export default ExerciseDetail
